import { CheckCircle } from '@untitledui/icons';
import { useTranslation } from 'react-i18next';
import type { DocumentResponse } from '@/types/api/documents';
import { FileTypeIcon } from './file-type-icon';
import { ExtractionStatusBadge } from './extraction-status-badge';
import { ValidationStatusBadge } from './validation-status-badge';

interface DocumentSidebarProps {
  documents: DocumentResponse[];
  selectedDocumentId: string | undefined;
  onSelectDocument: (documentId: string) => void;
  showValidationStatus?: boolean;
  showCategory?: boolean;
  showFormat?: boolean;
  collapsed?: boolean;
  title?: string;
}

// Get file format and color from filename or mime type
function getFileFormatInfo(doc: DocumentResponse): { format: string; color: string } {
  const ext = doc.file_name?.split('.').pop()?.toLowerCase();
  const mime = doc.file_mime_type;

  // PDF - red
  if (ext === 'pdf' || mime?.includes('pdf')) {
    return { format: 'PDF', color: 'text-error-500' };
  }

  // Images - green/blue
  if (ext === 'png' || mime?.includes('png')) {
    return { format: 'PNG', color: 'text-success-500' };
  }
  if (ext === 'jpg' || ext === 'jpeg' || mime?.includes('jpeg') || mime?.includes('jpg')) {
    return { format: 'JPG', color: 'text-success-500' };
  }
  if (ext === 'webp' || mime?.includes('webp')) {
    return { format: 'WEBP', color: 'text-success-500' };
  }
  if (ext === 'gif' || mime?.includes('gif')) {
    return { format: 'GIF', color: 'text-brand-500' };
  }

  // Other formats
  if (ext) {
    return { format: ext.toUpperCase(), color: 'text-tertiary' };
  }

  return { format: 'FILE', color: 'text-tertiary' };
}

// Check if document is validated (for collapsed mode indicator)
function isValidated(doc: DocumentResponse): boolean {
  return doc.validation_status === 'human_validated' || doc.validation_status === 'auto_validated';
}

// Format file size
function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentSidebar({
  documents,
  selectedDocumentId,
  onSelectDocument,
  showValidationStatus = true,
  showCategory: _showCategory = true,
  showFormat: _showFormat = true,
  collapsed = false,
  title,
}: DocumentSidebarProps) {
  const { t } = useTranslation(['pages:documents', 'common']);

  if (collapsed) {
    return (
      <div className="flex flex-col items-center py-2 px-1 gap-1.5">
        {documents.map((doc) => {
          const isSelected = doc.document_id === selectedDocumentId;
          const formatInfo = getFileFormatInfo(doc);

          return (
            <button
              key={doc.document_id}
              onClick={() => onSelectDocument(doc.document_id)}
              className={`relative px-1.5 py-1 rounded text-[10px] font-medium transition-colors ${isSelected ? 'text-brand-600' : ''
                } ${formatInfo.color}`}
              title={doc.file_name || undefined}
            >
              {formatInfo.format}
              {isValidated(doc) && (
                <div className="absolute -bottom-0.5 -right-0.5 bg-success-500 rounded-full p-0.5">
                  <CheckCircle className="size-2 text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex flex-col border-r border-secondary bg-secondary/20 h-full">
      <div className="p-2 flex-1 overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xs font-medium text-primary">
            {title || t('review.documents', { defaultValue: 'Documents' })}
          </h3>
          <span className="text-[10px] text-tertiary">{documents.length}</span>
        </div>

        <div className="space-y-0">
          {documents.map((doc, index) => {
            const isSelected = doc.document_id === selectedDocumentId;
            const fileSize = formatFileSize(doc.file_size_bytes);
            const isLast = index === documents.length - 1;

            return (
              <div key={doc.document_id}>
                <button
                  onClick={() => onSelectDocument(doc.document_id)}
                  className={`w-full text-left p-2 transition-colors ${isSelected ? 'text-brand-700' : 'text-primary hover:text-brand-600'
                    }`}
                >
                  {/* Riga principale: Icona + Nome */}
                  <div className="flex items-center gap-2">
                    <FileTypeIcon
                      type={doc.document_type || 'unknown'}
                      fileName={doc.file_name || undefined}
                      mimeType={doc.file_mime_type || undefined}
                      size="md"
                    />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm truncate ${isSelected ? 'font-medium text-brand-700' : 'text-primary'}`}>
                        {doc.file_name}
                      </div>
                    </div>
                  </div>

                  {/* Riga secondaria: Peso + Status badges */}
                  <div className="flex items-center justify-between mt-3">
                    {fileSize && (
                      <span className="text-[10px] text-tertiary">{fileSize}</span>
                    )}
                    {showValidationStatus && (
                      <div className="flex items-center gap-1">
                        <ExtractionStatusBadge status={doc.extraction_status} size="sm" showLabel={false} />
                        <ValidationStatusBadge status={doc.validation_status} size="sm" showLabel={false} />
                      </div>
                    )}
                  </div>
                </button>
                {!isLast && <div className="border-b border-secondary mx-2" />}
              </div>
            );
          })}

          {documents.length === 0 && (
            <div className="text-center py-3 text-[10px] text-tertiary">
              {t('review.noDocuments', { defaultValue: 'No documents found' })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DocumentSidebar;
